'use client';

import { useManyPerms, usePerms } from '@/hooks/usePerms';
import { useGetCurrentUser } from '@/providers/session-provider/use-current-user';
import { useFetchAllInstances } from '@/requests/hooks/use-fetch-all-instances';
import type {
  BaseInstance,
  ContentType,
  ModelName,
  MonitoringConfig,
  MonitoringConfiguration,
} from '@pigello/pigello-matrix';
import { AllMonitoringConfigs } from '@pigello/pigello-matrix';
import { useMemo } from 'react';

/**
 * Monitoring configs relevant for the approval/perform inbox.
 *
 * "Detailed logs" configurations (no approval fallback user, no approving
 * parties) always auto approve and auto perform in the backend, so they can
 * never produce pending approvals or performs and are skipped here.
 *
 * - approval: configurations where the current user is an approval candidate
 *   (fallback user or approving party), matching `is_relevant_for_approving`.
 * - perform: every approval flow, since the perform queue holds the user's own
 *   approved changes regardless of who approves them.
 */
export function useInboxMonitoringConfigs(kind: 'approval' | 'perform') {
  const { canView: canViewMonitoringConfigurations } = usePerms(
    'monitoringconfiguration'
  );
  const { data: currentUser } = useGetCurrentUser();
  const userId = currentUser?.id;

  const { data, isLoading: isLoadingConfigurations } =
    useFetchAllInstances<MonitoringConfiguration>({
      modelName: 'monitoringconfiguration',
      filters:
        kind === 'approval'
          ? { configuration_approval_candidates: { noop: userId } }
          : { approval_fallback_user: { __isnull: false } },
      enabled:
        !!canViewMonitoringConfigurations && (kind === 'perform' || !!userId),
    });

  // Disabled queries can retain data from a previously authorized session.
  const monitoringConfigs = canViewMonitoringConfigurations ? data : undefined;

  const contentTypes = useMemo(
    () =>
      monitoringConfigs?.list?.map((mc) => mc.forContentType.id as ContentType),
    [monitoringConfigs]
  );

  const configs = useMemo(() => {
    return Object.values(AllMonitoringConfigs).filter((c) =>
      contentTypes?.includes(c.parentContentType as ContentType)
    ) as MonitoringConfig<BaseInstance>[];
  }, [contentTypes]);

  const { perms, isFetchingPerms } = useManyPerms(
    useMemo(() => configs.map((c) => c.parentConfig as ModelName), [configs])
  );

  const relevantConfigs = useMemo(() => {
    return configs.filter(
      (c) => perms.find((p) => p.modelName === c.parentConfig)?.canView
    );
  }, [configs, perms]);

  const isLoading =
    !!canViewMonitoringConfigurations &&
    (isLoadingConfigurations ||
      isFetchingPerms ||
      (kind === 'approval' && !userId));

  return { configs: relevantConfigs, isLoading };
}
